import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import keyBy from 'lodash/keyBy'

import * as atypes from 'apps/ads/constants/AdsReduxActionTypes'
import { parseAccount } from 'apps/ads/models/AdData/adapterTyped'
import { AdAccountState } from 'apps/ads/models/adsInterfaces'
import { IThunkAction } from 'common/core/interfaces/actions'
import { AdsApi } from 'shared/api/requests/ads'
import { AdAccountsListResponse } from 'shared/api/requests/ads/schema'
import { createAsyncAction } from 'shared/lib/redux'

const NAMESPACE = 'adAccounts'

export const initialState: AdAccountState = {
  byId: {},
  fetched: false,
  loading: false,
  isTokenError: false,
}

export const fetchAdAccountsForIg = createAsyncAction(
  `${NAMESPACE}/fetchAdAccountsForIg`,
  async () => {
    const response = await AdsApi.fetchAdAccountsForIg()
    return response.data
  },
  {
    onBusinessError(error) {
      if (AdsApi.fetchAdAccountsForIg.isIsCustomAudienceActionTokenError(error)) {
        error.handle()
      }
    },
  },
)

interface LegacyAction<T> {
  $requestId: number
  data: T
  type: string
}

const adAccountsSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) =>
        action.type === atypes.AD_FETCH_ACCOUNTS_REQUEST ||
        fetchAdAccountsForIg.pending.match(action),
      (state) => {
        state.loading = true
      },
    )
    builder.addMatcher(
      (action) =>
        action.type === atypes.AD_FETCH_ACCOUNTS_RESPONSE ||
        fetchAdAccountsForIg.fulfilled.match(action),
      (
        state,
        action: PayloadAction<AdAccountsListResponse> | LegacyAction<AdAccountsListResponse>,
      ) => {
        const accounts =
          'payload' in action
            ? action.payload.data.map(parseAccount)
            : action.data.data.map(parseAccount)
        state.byId = keyBy(accounts, 'id')
        state.fetched = true
        state.loading = false
      },
    )
    builder.addMatcher(
      (action) =>
        action.type === atypes.AD_FETCH_ACCOUNTS_ERROR ||
        fetchAdAccountsForIg.rejected.match(action),
      (state) => {
        state.loading = false
        state.isTokenError = true
      },
    )
  },
})

export const ensureAdAccountsForIg = (): IThunkAction => (dispatch, getState) => {
  const { fetched } = getState().ads.account
  if (fetched) {
    return
  }
  return dispatch(fetchAdAccountsForIg())
}

const { reducer: adAccountsReducer } = adAccountsSlice

export { adAccountsReducer }
